import type {FC} from 'react'
import {useCallback, useMemo, useState} from 'react'
import type {
  IncludeExcludeParameters,
  RulesetTarget,
  SimpleOrganization,
  SourceType,
  WorkflowRepository,
  WorkflowsRuleMetadata,
} from '@github-ui/repos-rules-types/rules-types'
import {INCLUDE_ALL_PATTERN} from '@github-ui/repos-rules-types/rules-types'
import {announce} from '@github-ui/aria-live'
import {Dialog, type DialogButtonProps} from '@primer/react/experimental'
import {
  IncludeExcludeTarget,
  type IncludeExcludeTargetCustomAction,
  type IncludeExcludeTargetCustomDialogProps,
} from './IncludeExcludeTarget'
import {useRuleStrings} from '../../../hooks/use-rule-strings'
import {
  buildWorkflowMetadataMap,
  parseRequiredWorkflowTarget,
  RequiredWorkflowFileField,
  RequiredWorkflowOrganizationField,
  RequiredWorkflowRepositoryField,
  serializeRequiredWorkflowTarget,
  shouldShowRequiredWorkflowRepository,
  workflowMetadataKey,
  type WorkflowFile,
  type WorkflowMetadataEntry,
  type RequiredWorkflowTarget,
} from '../../rule-schema/RequiredWorkflowSelection'

export type WorkflowTargetProps = {
  rulesetId?: number
  readOnly: boolean
  fnmatchHelpUrl?: string
  rulesetTarget: RulesetTarget
  sourceType: SourceType
  parameters?: IncludeExcludeParameters
  metadata?: WorkflowsRuleMetadata
  updateParameters: (parameters: IncludeExcludeParameters | undefined) => void
}

const ALL_WORKFLOWS_PARAMETERS: IncludeExcludeParameters = {include: [INCLUDE_ALL_PATTERN], exclude: []}
const isEmpty = ({include, exclude}: IncludeExcludeParameters) => include.length === 0 && exclude.length === 0
const REQUIRED_WORKFLOW_ACTIONS: IncludeExcludeTargetCustomAction[] = (['include', 'exclude'] as const).map(
  includeOrExclude => ({
    type: `${includeOrExclude}_required_workflow`,
    text: `${includeOrExclude === 'include' ? 'Include' : 'Exclude'} required workflow`,
    actionGroup: 'Target a centrally managed required workflow',
    includeOrExclude,
  }),
)

export const WorkflowTarget: FC<WorkflowTargetProps> = ({
  rulesetId,
  readOnly,
  fnmatchHelpUrl,
  rulesetTarget,
  sourceType,
  parameters,
  metadata,
  updateParameters,
}) => {
  const {rulesetOrPolicy} = useRuleStrings()
  const isAllWorkflows = parameters === undefined
  const [authoredWorkflowMetadata, setAuthoredWorkflowMetadata] = useState<Record<string, WorkflowMetadataEntry>>({})
  const workflowMetadata = useMemo(
    () => ({...authoredWorkflowMetadata, ...buildWorkflowMetadataMap(metadata)}),
    [authoredWorkflowMetadata, metadata],
  )

  const onUpdateParameters = useCallback(
    (updatedParameters: IncludeExcludeParameters) => {
      // Only remove the projected entry, not a literal pattern entered by the user.
      const scopedParameters =
        isAllWorkflows && updatedParameters.include[0] === INCLUDE_ALL_PATTERN
          ? {...updatedParameters, include: updatedParameters.include.slice(1)}
          : updatedParameters
      const nextParameters = isEmpty(scopedParameters) ? undefined : scopedParameters

      updateParameters(nextParameters)

      if (!isAllWorkflows && nextParameters === undefined) {
        announce('All workflows are targeted')
      }
    },
    [isAllWorkflows, updateParameters],
  )

  const renderRequiredWorkflowDialog = useCallback(
    ({action, onAdd, onClose, returnFocusRef}: IncludeExcludeTargetCustomDialogProps) => (
      <RequiredWorkflowTargetDialog
        sourceType={sourceType}
        includeOrExclude={action.includeOrExclude}
        onAdd={(target, metadataEntry) => {
          setAuthoredWorkflowMetadata(current => ({
            ...current,
            [workflowMetadataKey(metadataEntry.repository.id, target.workflowPath)]: metadataEntry,
          }))
          onAdd(serializeRequiredWorkflowTarget(target))
        }}
        onClose={onClose}
        returnFocusRef={returnFocusRef}
      />
    ),
    [sourceType],
  )

  const getTargetPresentation = useCallback(
    ({value}: {value: string}) => {
      const target = parseRequiredWorkflowTarget(value)
      if (!target) {
        return {}
      }

      const metadataEntry = workflowMetadata[workflowMetadataKey(target.repositoryId, target.workflowPath)]
      const repositoryName = metadataEntry
        ? [metadataEntry.repository.ownerLogin, metadataEntry.repository.name].filter(Boolean).join('/')
        : `Repository ID ${target.repositoryId}`
      const workflowName = metadataEntry?.workflowName || target.workflowPath.split('/').at(-1) || 'Required workflow'
      const removeLabel = `${workflowName}, ${target.workflowPath}, in ${repositoryName}`

      return {
        prefix: 'required workflow',
        removeLabel,
        customDisplay: (
          <div className="d-flex flex-column">
            <span className="text-bold">{workflowName}</span>
            <span className="text-small color-fg-muted">
              {repositoryName} · {target.workflowPath}
            </span>
          </div>
        ),
      }
    },
    [workflowMetadata],
  )

  return (
    <IncludeExcludeTarget
      rulesetId={rulesetId}
      readOnly={readOnly}
      rulesetTarget={rulesetTarget}
      fnmatchHelpUrl={fnmatchHelpUrl}
      parameters={parameters ?? ALL_WORKFLOWS_PARAMETERS}
      implicitAllLabel={isAllWorkflows ? 'All workflows' : undefined}
      panelTitle="Workflow targeting criteria"
      targetType="workflow_path"
      updateParameters={onUpdateParameters}
      customActions={sourceType === 'repository' ? undefined : REQUIRED_WORKFLOW_ACTIONS}
      renderCustomDialog={sourceType === 'repository' ? undefined : renderRequiredWorkflowDialog}
      getTargetPresentation={getTargetPresentation}
      blankslate={{
        heading: 'No workflow targets have been added yet',
        description: !readOnly ? (
          <>
            Add an include or exclude pattern to target workflow files with this {rulesetOrPolicy}. Patterns match the
            full path, for example <code>.github/workflows/deploy.yml</code>.
          </>
        ) : undefined,
      }}
    />
  )
}

function RequiredWorkflowTargetDialog({
  sourceType,
  includeOrExclude,
  onAdd,
  onClose,
  returnFocusRef,
}: {
  sourceType: SourceType
  includeOrExclude: 'include' | 'exclude'
  onAdd: (target: RequiredWorkflowTarget, metadata: WorkflowMetadataEntry) => void
  onClose: () => void
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
}) {
  const [organization, setOrganization] = useState<SimpleOrganization>()
  const [repository, setRepository] = useState<WorkflowRepository>()
  const [workflowPath, setWorkflowPath] = useState('')
  const [selectedWorkflow, setSelectedWorkflow] = useState<{repositoryId: number; workflow: WorkflowFile}>()
  const [showMissing, setShowMissing] = useState(false)
  const workflowMissing = !selectedWorkflow || selectedWorkflow.repositoryId !== repository?.id

  const onOrganizationChange = useCallback(
    (nextOrganization?: SimpleOrganization) => {
      if (nextOrganization?.id !== organization?.id) {
        setOrganization(nextOrganization)
        setRepository(undefined)
        setWorkflowPath('')
        setSelectedWorkflow(undefined)
        setShowMissing(false)
      }
    },
    [organization],
  )

  const onRepositoryChange = useCallback((nextRepository?: WorkflowRepository) => {
    setRepository(nextRepository)
    setWorkflowPath('')
    setSelectedWorkflow(undefined)
    setShowMissing(false)
  }, [])

  const saveWorkflow = useCallback(() => {
    if (sourceType === 'enterprise' && !organization) {
      setShowMissing(true)
      announce('Please select an organization')
      return
    }
    if (!repository) {
      setShowMissing(true)
      announce('Please select a repository')
      return
    }
    if (workflowMissing) {
      setShowMissing(true)
      announce('Please select a workflow')
      return
    }

    onAdd(
      {repositoryId: repository.id, workflowPath: selectedWorkflow.workflow.path},
      {repository, workflowName: selectedWorkflow.workflow.name},
    )
  }, [onAdd, organization, repository, selectedWorkflow, sourceType, workflowMissing])

  const footerButtons: DialogButtonProps[] = useMemo(
    () => [
      {content: 'Cancel', onClick: onClose, buttonType: 'normal'},
      {content: 'Add required workflow', onClick: saveWorkflow, buttonType: 'primary'},
    ],
    [onClose, saveWorkflow],
  )

  const title = `${includeOrExclude === 'include' ? 'Include' : 'Exclude'} required workflow`

  return (
    <Dialog
      aria-label={title}
      title={title}
      subtitle={
        <span className="text-normal text-small color-fg-muted">
          Choose a centrally managed required workflow without entering its internal execution path.
        </span>
      }
      footerButtons={footerButtons}
      height="auto"
      width="medium"
      onClose={onClose}
      returnFocusRef={returnFocusRef}
    >
      {sourceType === 'enterprise' && (
        <RequiredWorkflowOrganizationField
          organization={organization}
          onChange={onOrganizationChange}
          showError={showMissing && !organization}
          showLoadError
        />
      )}
      {shouldShowRequiredWorkflowRepository(sourceType, organization) && (
        <RequiredWorkflowRepositoryField
          key={organization?.id ?? sourceType}
          organization={organization}
          repository={repository}
          onChange={onRepositoryChange}
          showError={showMissing && !repository}
          showLoadError
        />
      )}
      {repository && (
        <RequiredWorkflowFileField
          repository={repository}
          path={workflowPath}
          onChange={(path, workflow) => {
            setWorkflowPath(path)
            setSelectedWorkflow(workflow ? {repositoryId: repository.id, workflow} : undefined)
            setShowMissing(false)
          }}
          validation={showMissing && workflowMissing ? 'Please select a workflow' : undefined}
          showLoadError
          targetableOnly
        />
      )}
    </Dialog>
  )
}
